import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Observable, Subscription } from "rxjs";

import { PinAutorizadorService } from "src/app/security/pin-autorizador/pin-autorizador.service";
import { BscRes } from "src/app/shared/model/bsc/bsc.model";
import { PIN_MAX, PIN_MIN, PinAutorizadorRes, soloDigitos } from "src/app/shared/model/security/pin-autorizador.model";

/** Lo que recibe el cuadro: qué se resuelve y cómo se manda. */
export interface DatosDialogoPinBsc {
  /** true autoriza (comentario opcional); false regresa (comentario obligatorio). */
  aprobar: boolean;
  /** Arriba del título: "Autorizar planeación", "Regresar planeación". */
  eyebrow: string;
  /** "Desarrollo · planeación 2027". */
  titulo: string;
  /** Qué pasa al confirmar, en una o dos frases. */
  detalle: string;
  /** El texto del botón que confirma. */
  textoConfirmar: string;
  /**
   * (F6.6) El comentario con que abre el cuadro: C2 lo trae de su campo
   * «Comentario para el área». Se puede corregir aquí; el que viaja es el del cuadro.
   */
  comentarioInicial?: string;
  /**
   * (F6.6) Otro comentario que el de autorizar/regresar: el motivo de una
   * reapertura (obligatorio aunque no se regrese). Sin él, el de `aprobar`.
   */
  comentario?: ComentarioDialogoPinBsc;
  /** (F6.6) El icono de la cabecera; sin él, el de `aprobar` (verified / undo). */
  icono?: string;
  /**
   * (F6.6) Errores del servidor que no se corrigen en el cuadro (otro ya lo
   * resolvió: VAL-BSC-CONFLICTO; el año se cerró…): el cuadro se cierra al
   * momento con esa respuesta, sin esperar a Cancelar, y quien lo abre la
   * dice y vuelve a consultar. Sin la lista, como T3: se enseñan aquí.
   */
  cerrarAnte?: string[];
  /**
   * La petición con el PIN y el comentario (ya sin espacios en los extremos;
   * vacío si no se escribió). El cuadro enseña su codigoError y deja
   * corregir; sin error se cierra con la respuesta. Si se cancela después de
   * un error que no es del PIN, se cierra con esa respuesta (ver
   * abrirDialogoPinBsc).
   */
  enviar: (pin: string, comentario: string) => Observable<BscRes>;
}

/** El campo del comentario cuando no es el de autorizar/regresar (F6.6: reabrir). */
export interface ComentarioDialogoPinBsc {
  obligatorio: boolean;
  /** "Motivo de la reapertura". */
  etiqueta: string;
  /** Debajo del campo: "Obligatorio: queda en la bitácora…". */
  ayuda: string;
  /** Lo que se dice si falta: "Escribe por qué se reabre…". */
  falta: string;
}

/** Los de autorizar (opcional) y regresar (obligatorio), los de T3 desde F5. */
const COMENTARIO_APROBAR: ComentarioDialogoPinBsc = {
  obligatorio: false,
  etiqueta: "Comentario (opcional)",
  ayuda: "Queda en la bitácora.",
  falta: "",
};
const COMENTARIO_REGRESAR: ComentarioDialogoPinBsc = {
  obligatorio: true,
  etiqueta: "Por qué se regresa",
  ayuda: "Obligatorio: el área lo verá al corregir.",
  falta: "Escribe por qué se regresa: el área lo verá al corregir.",
};

/** El campo del comentario de un cuadro: el que pidió quien lo abre o el de `aprobar`. */
function comentarioDe(datos: DatosDialogoPinBsc): ComentarioDialogoPinBsc {
  return datos.comentario ?? (datos.aprobar ? COMENTARIO_APROBAR : COMENTARIO_REGRESAR);
}

/** Tope de los comentarios de resolución (BscPlaneacionBuss.LARGO_MOTIVO). */
const LARGO_COMENTARIO = 1000;
const ID_TITULO = "bsc-pin-titulo";
const ID_PIN = "bsc-pin-campo";
const ID_COMENTARIO = "bsc-pin-comentario";

/** Errores del PIN que cambian su estado (intentos, bloqueo, sin PIN): se vuelve a preguntar. */
const ERRORES_DE_PIN = /^VAL-PIN|^VAL-PERFIL$/;

/**
 * Abre el cuadro del PIN con la forma de los diálogos estándar
 * (app-standard-dialog: la superficie de Material transparente y la caja
 * .app-dialog de styles.css, que tema-overlays.css pinta en oscuro y
 * cristal). El foco entra al comentario si es obligatorio y viene vacío, y
 * si no al PIN.
 *
 * @returns el cuadro: afterClosed() da la respuesta del servidor si se
 *          resolvió; undefined si se canceló sin que el servidor contestara
 *          un error; y, si se canceló después de un error que no es del PIN
 *          (VAL-BSC-CONFLICTO porque otro ya la resolvió, VAL-BSC-CERRADO,
 *          AUTH-001…), esa última respuesta con su codigoError: lo que enseña
 *          la pantalla ya no vale y quien lo abre vuelve a consultar. Con
 *          `cerrarAnte` (F6.6), esos errores lo cierran al llegar. Quien lo
 *          abre lo cierra si su pantalla se destruye.
 */
export function abrirDialogoPinBsc(
  dialog: MatDialog,
  datos: DatosDialogoPinBsc,
): MatDialogRef<DialogoPinBscComponent, BscRes> {
  const alComentario = comentarioDe(datos).obligatorio && !(datos.comentarioInicial ?? "").trim();
  return dialog.open<DialogoPinBscComponent, DatosDialogoPinBsc, BscRes>(DialogoPinBscComponent, {
    data: datos,
    width: "520px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    panelClass: "app-standard-dialog",
    backdropClass: "app-standard-dialog-backdrop",
    ariaLabelledBy: ID_TITULO,
    autoFocus: alComentario ? `#${ID_COMENTARIO}` : `#${ID_PIN}`,
    // Escape y el fondo pasan por cancelar(): con la petición en vuelo no cierran.
    disableClose: true,
  });
}

/**
 * BSC · el cuadro para autorizar o regresar con PIN (T3 en F5; C2 lo
 * reutiliza en F6.6 para autorizar, regresar y reabrir los trimestres, con
 * el comentario que ya traía escrito y, al reabrir, el campo del motivo).
 *
 * No existía un componente de PIN reutilizable: Homologación de Modelos IA
 * lo tiene dentro de su plantilla (#campoPin y el cuadro .hom-modal) y
 * Evaluaciones IA usa SweetAlert con input password, que no admite el
 * comentario obligatorio de "regresar". Este calca el de Homologación: el
 * estado del PIN con PinAutorizadorService.estado() (sin PIN → se define en
 * Ajustes, en otra pestaña, y "Ya lo definí" vuelve a preguntar; bloqueado →
 * hasta qué hora; los intentos que quedan), el campo solo con dígitos
 * (soloDigitos) y el error del servidor dentro del cuadro, con el PIN
 * borrado y el foco de vuelta en él. Quien abre el cuadro hace la petición
 * en `enviar`; el servidor revisa la llave, el PIN y el comentario.
 */
@Component({
  selector: "app-dialogo-pin-bsc",
  standalone: true,
  templateUrl: "./dialogo-pin-bsc.component.html",
  styleUrls: ["./dialogo-pin-bsc.component.css"],
  imports: [CommonModule, FormsModule, MatDialogContent, MatDialogActions, MatFormFieldModule, MatInputModule],
})
export class DialogoPinBscComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly idPin = ID_PIN;
  readonly idComentario = ID_COMENTARIO;
  readonly largoComentario = LARGO_COMENTARIO;
  /** El campo del comentario: el de autorizar/regresar o el que pidió quien abre (reabrir). */
  readonly campoComentario: ComentarioDialogoPinBsc;
  readonly icono: string;
  readonly pinMax = PIN_MAX;
  readonly pinMin = PIN_MIN;
  /** Ajustes, donde cada quien define su PIN (PinAutorizadorPanelComponent). */
  readonly rutaAjustes = "#/admin/ajustes";

  pin = "";
  comentario = "";
  enviando = false;
  /** El error del servidor o lo que falta para confirmar. */
  mensaje = "";
  /** Mi PIN: si lo tengo, si está bloqueado y cuántos intentos quedan; nulo mientras se pregunta o si no se supo. */
  estadoPin: PinAutorizadorRes | null = null;
  consultandoPin = false;

  private readonly subs: Subscription[] = [];
  private cerrado = false;
  /**
   * El último error del servidor que no es del PIN: lo resuelto cambió (otro
   * se adelantó, el año se cerró) o la sesión caducó. Cancelar lo devuelve.
   */
  private errorDelServidor: BscRes | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosDialogoPinBsc,
    private readonly dialogRef: MatDialogRef<DialogoPinBscComponent, BscRes>,
    private readonly pinService: PinAutorizadorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    this.campoComentario = comentarioDe(datos);
    this.icono = datos.icono ?? (datos.aprobar ? "verified" : "undo");
    this.comentario = (datos.comentarioInicial ?? "").slice(0, LARGO_COMENTARIO);
  }

  ngOnInit(): void {
    this.subs.push(
      this.dialogRef.backdropClick().subscribe(() => this.cancelar()),
      this.dialogRef.keydownEvents().subscribe((ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          this.cancelar();
        }
      }),
    );
    this.revisarPin();
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /** Lo vuelve a preguntar: por si lo definió en Ajustes, en otra pestaña. */
  revisarPin(): void {
    this.consultandoPin = true;
    this.cdr.markForCheck();
    this.subs.push(
      this.pinService.estado().subscribe({
        next: (res) => {
          this.consultandoPin = false;
          // Si no se pudo saber, se deja escribir: el servidor revisa el PIN igual.
          this.estadoPin = res?.codigoError ? null : res;
          this.pinta();
          // Sin PIN o bloqueado, el campo donde estaba el foco ya no existe:
          // va al primer botón del aviso, dentro del cuadro.
          if ((this.sinPin || this.pinBloqueado) && !this.host.nativeElement.contains(document.activeElement)) {
            setTimeout(() =>
              this.host.nativeElement.querySelector<HTMLElement>(".bsc-pin-dialog__botones .app-form-btn")?.focus(),
            );
          }
        },
        error: () => {
          this.consultandoPin = false;
          this.estadoPin = null;
          this.pinta();
        },
      }),
    );
  }

  get sinPin(): boolean {
    return !!this.estadoPin && this.estadoPin.tienePin !== true;
  }

  get pinBloqueado(): string | null {
    return this.estadoPin?.tienePin && this.estadoPin.bloqueadoHasta ? this.estadoPin.bloqueadoHasta : null;
  }

  /** Los intentos que quedan cuando ya se equivocó alguna vez (cinco fallos bloquean 15 minutos). */
  get avisoIntentos(): string {
    const n = this.estadoPin?.intentosRestantes;
    if (n == null || n >= 5) {
      return "Con cinco fallos se bloquea 15 minutos.";
    }
    const quedan = n === 1 ? "queda 1 intento" : `quedan ${n} intentos`;
    return `Te ${quedan} antes de que se bloquee 15 minutos.`;
  }

  get faltaComentario(): boolean {
    return this.campoComentario.obligatorio && !this.comentario.trim();
  }

  get pinCompleto(): boolean {
    return new RegExp(String.raw`^\d{${PIN_MIN},${PIN_MAX}}$`).test(this.pin);
  }

  get listo(): boolean {
    return !this.enviando && !this.sinPin && !this.pinBloqueado && this.pinCompleto && !this.faltaComentario;
  }

  /** Solo dígitos, hasta 8: se corrige el propio campo para que una letra no se quede a la vista. */
  digitos(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const limpio = soloDigitos(input.value);
    if (input.value !== limpio) {
      input.value = limpio;
    }
    this.pin = limpio;
    this.mensaje = "";
  }

  confirmar(): void {
    if (this.enviando) {
      return;
    }
    if (!this.listo) {
      if (this.faltaComentario) {
        this.mensaje = this.campoComentario.falta;
        this.enfoca(ID_COMENTARIO);
      } else if (!this.sinPin && !this.pinBloqueado && !this.pinCompleto) {
        this.mensaje = `Tu PIN va de ${PIN_MIN} a ${PIN_MAX} dígitos.`;
        this.enfoca(ID_PIN);
      }
      return;
    }
    this.enviando = true;
    this.mensaje = "";
    this.pinta();
    this.subs.push(
      this.datos.enviar(this.pin, this.comentario.trim()).subscribe({
        next: (res) => {
          this.enviando = false;
          if (!res || res.codigoError) {
            // El PIN equivocado no se queda escrito.
            this.pin = "";
            this.mensaje = this.textoDeError(res);
            if (res?.codigoError && ERRORES_DE_PIN.test(res.codigoError)) {
              this.revisarPin();
            } else if (res?.codigoError && this.datos.cerrarAnte?.includes(res.codigoError)) {
              this.cerrado = true;
              this.dialogRef.close(res);
              return;
            } else if (res?.codigoError) {
              this.errorDelServidor = res;
            }
            this.pinta();
            this.enfoca(ID_PIN);
            return;
          }
          this.cerrado = true;
          this.dialogRef.close(res);
        },
        error: () => {
          this.enviando = false;
          this.pin = "";
          this.mensaje = "Sin conexión con el servidor. No se guardó nada.";
          this.pinta();
          this.enfoca(ID_PIN);
        },
      }),
    );
  }

  cancelar(): void {
    if (this.enviando || this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(this.errorDelServidor ?? undefined);
  }

  private textoDeError(res: BscRes | null | undefined): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || "No se pudo resolver. No se guardó nada.";
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }

  /** Después de pintar: el campo pudo estar deshabilitado mientras se mandaba. */
  private enfoca(id: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(`#${id}`)?.focus());
  }
}
